import { Box, Text } from '@nimbus-ds/components';
import { TFunction } from 'i18next';
import { CurrencyType, PaymentResponseDto } from '@tiendanube/common';
import { Country, Status as StatusEnum } from '@tiendanube/common/src/enums';
import { Button, Label, Link } from '@tiendanube/components';
import { ExternalLinkIcon, InvoiceIcon } from '@tiendanube/icons';
import { CurrencyPrice } from 'commons/components';
import { getShouldShowInstallments } from 'domains/Orders/Orders/utils/OrderDetailsPage/utils';

interface PaymentMethodDetailProps {
  payment: PaymentResponseDto;
  currency: CurrencyType;
  t: TFunction;
  openGatewayLink: (gatewayLink: string) => void;
  rejectedPaymentInfo: string;
  isMultiplePayments: boolean;
  isDesktop: boolean;
  isNuvemPagoPayment: boolean;
  onNuvemPagoRedirect: () => void;
  country: Country;
  isMobile: boolean;
  openNuvemPago: () => void;
}

function PaymentMethodDetail({
  payment: {
    gateway,
    method,
    installments,
    capturedAmount,
    refundedAmount,
    gatewayId,
    status,
    gatewayLink,
  },
  currency,
  t,
  openGatewayLink,
  rejectedPaymentInfo,
  isMultiplePayments,
  isDesktop,
  isNuvemPagoPayment,
  onNuvemPagoRedirect,
  country,
  isMobile,
  openNuvemPago,
}: Readonly<PaymentMethodDetailProps>): JSX.Element {
  const showInstallments = getShouldShowInstallments(
    installments ?? '0',
    method,
  );

  const gatewayIdText = (
    <Text as={isDesktop ? 'span' : 'p'}>
      {t('cardPayment.gatewayId')}
      {gatewayId}
    </Text>
  );

  const gatewayLinkText = gatewayLink ? (
    <Link
      appearance="primary"
      icon={ExternalLinkIcon}
      iconPosition="end"
      onClick={() => openGatewayLink(gatewayLink)}
    >
      {gatewayId}
    </Link>
  ) : (
    <Text as={isDesktop ? 'span' : 'p'}>{gatewayId}</Text>
  );

  const hasAnyRefund = Number(refundedAmount?.value ?? 0) > 0;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="1"
      width="full"
      paddingTop="3"
    >
      {!isMultiplePayments && (
        <Box marginBottom="1">
          <Text as="p" color="neutral-textHigh" fontWeight="medium">{`${t(
            `gateway.${gateway}`,
            gateway ?? '',
          )}`}</Text>
        </Box>
      )}
      <Box display="flex" alignItems="center">
        {method !== '' && (
          <Box display="block" paddingY="2" paddingRight="2">
            <Label id={method} label={method} />
          </Box>
        )}
        {showInstallments && (
          <Text as="p" color="neutral-textHigh">
            {t('cardPayment.installments', {
              count: Number(installments),
            })}
          </Text>
        )}
      </Box>
      {isMultiplePayments && (
        <Box paddingY="1">
          <CurrencyPrice
            price={Number(capturedAmount?.value ?? 0)}
            currency={currency}
          />
        </Box>
      )}
      {!!gatewayId && (
        <>
          <Text color="neutral-textHigh" as={isDesktop ? 'span' : 'p'}>
            {status !== StatusEnum.WARNIG_TRANSACTIONS
              ? t('cardPayment.gatewayId')
              : rejectedPaymentInfo}
          </Text>

          {isNuvemPagoPayment &&
          country === Country.AR &&
          status === StatusEnum.WARNIG_TRANSACTIONS
            ? gatewayIdText
            : gatewayLinkText}
        </>
      )}
      {isNuvemPagoPayment &&
        status === StatusEnum.WARNIG_TRANSACTIONS &&
        (country === Country.BR && isMobile ? (
          <Text as="p">
            {t('cardPayment.rejectedNuvemPagoPaymentWithoutLink')}
          </Text>
        ) : (
          <Text as="p">
            {t('cardPayment.rejectedNuvemPagoPaymentInfo1')}{' '}
            <Link
              appearance="primary"
              onClick={
                gatewayLink ? () => openGatewayLink(gatewayLink) : openNuvemPago
              }
            >
              {t('cardPayment.rejectedNuvemPagoPaymentInfo2')}
            </Link>{' '}
            {t('cardPayment.rejectedNuvemPagoPaymentInfo3')}
          </Text>
        ))}
      {isNuvemPagoPayment && hasAnyRefund && (
        <Box display="block" paddingY="2">
          <Button
            icon={InvoiceIcon}
            appearance="default"
            onClick={onNuvemPagoRedirect}
          >
            {t('cardPayment.refundReceipt')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default PaymentMethodDetail;
